import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Box, Button, CircularProgress, FormControl, FormHelperText, InputLabel,
    MenuItem, Select, TextField, Typography
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SearchBox from '../quizzes/SearchBox';
import { getFanPollCategories } from './fanPollApi';
import { FAN_POLL_TYPES } from './fanPollModel';

const toArtistTag = (entity) => entity ? ({
    label: entity.label,
    idMapping: { WIKIDATA: entity.id },
}) : null;

const toSearchEntity = (mainTag) => {
    const id = mainTag?.idMapping?.WIKIDATA;
    return id ? { id, label: mainTag.label, type: 'artist' } : null;
};

export default function FanPollTopicSelector({ draft, errors, onChange, onTopicSelected, onInspire, inspiring }) {
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [categoryError, setCategoryError] = useState('');

    useEffect(() => {
        if (draft.pollType !== 'GENRE_DECADE' || categories.length) return;
        let active = true;
        setLoadingCategories(true);
        setCategoryError('');
        getFanPollCategories()
            .then(result => { if (active) setCategories(Array.isArray(result) ? result : []); })
            .catch(error => { if (active) setCategoryError(error.message ?? String(error)); })
            .finally(() => { if (active) setLoadingCategories(false); });
        return () => { active = false; };
    }, [draft.pollType, categories.length]);

    const changeType = (pollType) => onTopicSelected({
        ...draft,
        pollType,
        mainTag: null,
        tagList: [],
    });

    const selectTopic = (mainTag) => onTopicSelected({
        ...draft,
        mainTag,
        tagList: mainTag ? [mainTag] : [],
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl size="small" error={Boolean(errors.pollType)}>
                <InputLabel>Poll type</InputLabel>
                <Select value={draft.pollType} label="Poll type" onChange={event => changeType(event.target.value)}>
                    {FAN_POLL_TYPES.map(type => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                </Select>
                {errors.pollType && <FormHelperText>{errors.pollType}</FormHelperText>}
            </FormControl>

            {draft.pollType === 'ARTISTS' && (
                <SearchBox
                    small
                    value={toSearchEntity(draft.mainTag)}
                    endPoint="entity"
                    defaultText="Search artist or group"
                    onEntitySelected={entity => selectTopic(toArtistTag(entity))}
                    resultMappingFunction={result => ({
                        label: result.label,
                        id: result.idMapping?.WIKIDATA ?? result.id ?? result.wikidataId,
                        type: result.type,
                        seoTitle: result.seoTitle,
                    })}
                />
            )}

            {draft.pollType === 'GENRE_DECADE' && (
                <Autocomplete
                    value={draft.mainTag}
                    options={categories}
                    loading={loadingCategories}
                    getOptionLabel={option => option?.label ?? ''}
                    isOptionEqualToValue={(option, value) => option?.label === value?.label}
                    onChange={(_, value) => selectTopic(value)}
                    renderInput={params => (
                        <TextField
                            {...params}
                            size="small"
                            label="Select genre or decade"
                            error={Boolean(errors.mainTag || categoryError)}
                            helperText={errors.mainTag || categoryError}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingCategories ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            )}

            {draft.pollType === 'CUSTOM' && (
                <Typography variant="caption" color="text.secondary">
                    Select the primary artist, genre, or decade in the tags section below.
                </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Poll subject"
                    value={draft.subject}
                    onChange={event => onChange({ ...draft, subject: event.target.value })}
                    error={Boolean(errors.subject)}
                    helperText={errors.subject || 'Describe the poll angle, for example: studio albums, 1980s hits, or live performances.'}
                />
                <Button variant="outlined" startIcon={inspiring ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    onClick={onInspire} disabled={!draft.mainTag || inspiring} sx={{ minWidth: 112, height: 40 }}>
                    Inspire
                </Button>
            </Box>
        </Box>
    );
}
